import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { validateToken, clearAllTokens } from '@/utils/tokenManager';
import { getEvaluatorById } from '@/services/evaluator';
import { handleEvaluationSubmission } from '@/services/evaluationService';
import { getAutomationById } from '@/services/automation';
import { doc, getDoc } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { Link } from 'react-router-dom';
import Logo from '@/components/Logo';
import { toast } from 'react-hot-toast';

export default function EvaluationForm() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [evaluator, setEvaluator] = useState<any>(null);
  const [formUrl, setFormUrl] = useState<string>('');

  useEffect(() => {
    async function validateAndLoad() {
      if (!token) {
        navigate('/');
        return;
      }

      try {
        const tokenData = validateToken(token);
        if (!tokenData) {
          throw new Error('Invalid access token');
        }

        // First get the evaluator data
        const evaluatorId = tokenData.evaluatorId.split('_')[1]; // Extract original evaluator ID
        const evaluatorData = await getEvaluatorById(evaluatorId);

        if (!evaluatorData) {
          throw new Error('Evaluator not found');
        }

        // Then get the automation and user data using evaluator's userId
        const [automation, userDoc] = await Promise.all([
          getAutomationById('evaluation-form'),
          getDoc(doc(db, collections.users, evaluatorData.userId))
        ]);

        if (!automation) {
          throw new Error('Evaluation form configuration not found');
        }

        if (!evaluatorData.emailVerified) {
          throw new Error('Email not verified');
        }

        // Get the user's selected plan ID
        const userData = userDoc?.exists() ? userDoc.data() : null;
        const selectedPlanId = userData?.selectedPlanId || '';

        if (evaluatorData.evaluationCompleted) {
          throw new Error('This evaluation has already been completed');
        }

        setEvaluator(evaluatorData);

        const url = new URL(automation.url);
        url.searchParams.set('evaluator_email', evaluatorData.email);
        url.searchParams.set('subject_name', evaluatorData.subjectName.split(' ')[0]);
        url.searchParams.set('evaluator_id', evaluatorData.id);
        url.searchParams.set('user_id', evaluatorData.userId);
        url.searchParams.set('planid', selectedPlanId);
        
        setFormUrl(url.toString());
      } catch (error: any) {
        console.error('Error loading evaluation form:', error);
        const errorMessage = error.message || 'Failed to load evaluation form';
        setError(errorMessage);
        toast.error(errorMessage);
        setTimeout(() => navigate('/'), 3000);
      } finally {
        setLoading(false);
      }
    }

    validateAndLoad();
  }, [token, navigate]);

  useEffect(() => {
    async function handleFormSubmission(event: MessageEvent) {
      if (event.data.type === 'form-submit' && evaluator) {
        try {
          await handleEvaluationSubmission(evaluator.id, evaluator.userId);
          clearAllTokens(); // Clear tokens after successful submission
          toast.success('Thank you for completing the evaluation!');
          navigate(`/thankyou/${evaluator.id}`);
        } catch (error: any) {
          console.error('Error submitting evaluation:', error);
          toast.error(error.message || 'Failed to submit evaluation');
        }
      }
    }

    window.addEventListener('message', handleFormSubmission);
    return () => window.removeEventListener('message', handleFormSubmission);
  }, [evaluator, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <Logo className="h-12 mb-8" />
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg max-w-md">
          {error}
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50 flex flex-col"
    >
      <div className="flex justify-center pt-8 pb-6">
        <Link to="/">
          <Logo className="h-12" />
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full h-[calc(100vh-120px)] rounded-xl overflow-hidden shadow-xl border border-gray-200 px-0 sm:px-0"
        >
          <iframe
            className="typeform-widget w-full h-full"
            src={formUrl}
            style={{ border: 'none', maxWidth: 'none' }}
            title="Evaluation Form"
            frameBorder="0"
            allow="camera; microphone; autoplay; encrypted-media; fullscreen"
          />
        </motion.div>
      </div>
    </motion.div>
  );
}